import { useState, useRef, useEffect } from 'react';
import { Send, Paperclip, Mic, Image, FileText, Loader2, X } from 'lucide-react';
import { ChatMessage } from '../components/ChatMessage';
import { PatternViewer } from '../components/PatternViewer';
import { Viewer3D } from '../components/Viewer3D';
import { useChat } from '../hooks/useChat';
import { usePatternStore } from '../stores/patternStore';
import type { PatternPiece } from '../types';
import clsx from 'clsx';

export function ChatPage() {
  const [input, setInput] = useState('');
  const [activeTab, setActiveTab] = useState<'pattern' | '3d'>('pattern');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { messages, isLoading, sendMessage, uploadFile, uploadedFile, currentJob } = useChat();
  const { setPieces } = usePatternStore();

  // When a message with a garmentSpec arrives (streaming complete), generate demo pattern pieces
  useEffect(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      // Only act on finalized (non-streaming) assistant messages with a garmentSpec
      if (msg.role === 'assistant' && !msg.streaming && msg.patternGenerated && msg.garmentSpec) {
        const pieces = garmentSpecToPatternPieces(msg.garmentSpec);
        if (pieces.length > 0) {
          setPieces(pieces);
        }
        break;
      }
    }
  }, [messages, setPieces]);

  /**
   * Convert a GarmentSpec JSON into PatternPiece objects for the viewer.
   * Uses simplified geometry based on the spec — the real geometry kernel
   * would produce precise pieces, but this gives immediate visual feedback.
   */
  function garmentSpecToPatternPieces(spec: Record<string, any>): PatternPiece[] {
    const pieces: PatternPiece[] = [];
    const type = (spec.garmentType || spec.type || 'bodice').toLowerCase();
    const m = spec.measurements || {};
    const bust = m.bust || 88;
    const waist = m.waist || 70;
    const hip = m.hip || 96;
    const ease = spec.ease || {};
    const bustEase = ease.bust || 8;
    const waistEase = ease.waist || 4;

    // Scale: 1 unit = 1mm, so 1cm = 10 units
    const scale = 10;

    if (type.includes('bodice') || type.includes('dress') || type.includes('top') || type.includes('shirt') || type.includes('blouse')) {
      const bW = ((bust + bustEase) / 4) * scale;
      const wW = ((waist + waistEase) / 4) * scale;
      const length = 42 * scale; // 42cm bodice length

      // Front bodice
      pieces.push({
        id: 'bodice-front',
        name: 'Bodice Front',
        path: `M 0,0 L ${bW},0 Q ${bW + 10},20 ${bW + 5},40 L ${wW + 5},${length} Q ${wW},${length + 10} 0,${length} Z`,
        labelPos: { x: bW / 2, y: length / 2 },
        seamAllowance: 1.5,
        grainline: { x1: bW / 2, y1: 30, x2: bW / 2, y2: length - 20 },
        notches: [{ x: bW + 5, y: 45, angle: Math.PI / 4 }],
      });

      // Back bodice
      pieces.push({
        id: 'bodice-back',
        name: 'Bodice Back',
        path: `M ${bW * 2 + 30},0 L ${bW * 3 + 30},0 Q ${bW * 3 + 35},15 ${bW * 3 + 30},35 L ${wW * 3 + 35},${length} Q ${wW * 3 + 30},${length + 10} ${bW * 2 + 30},${length} Z`,
        labelPos: { x: bW * 2.5 + 30, y: length / 2 },
        seamAllowance: 1.5,
        grainline: { x1: bW * 2.5 + 30, y1: 30, x2: bW * 2.5 + 30, y2: length - 20 },
      });

      // Add sleeve if spec says set-in
      const sleeveType = spec.style?.sleeveType || '';
      if (!sleeveType.includes('sleeveless') && !sleeveType.includes('none')) {
        const sleeveW = 18 * scale;
        const sleeveL = 60 * scale;
        const capH = 15 * scale;
        const offsetX = bW * 4 + 60;
        pieces.push({
          id: 'sleeve',
          name: 'Sleeve',
          path: `M ${offsetX},${capH} Q ${offsetX + sleeveW / 2},0 ${offsetX + sleeveW},${capH} L ${offsetX + sleeveW - 10},${sleeveL} Q ${offsetX + sleeveW / 2},${sleeveL + 10} ${offsetX + 10},${sleeveL} Z`,
          labelPos: { x: offsetX + sleeveW / 2, y: sleeveL / 2 },
          seamAllowance: 1,
          grainline: { x1: offsetX + sleeveW / 2, y1: capH + 20, x2: offsetX + sleeveW / 2, y2: sleeveL - 20 },
          notches: [
            { x: offsetX + sleeveW / 2, y: capH, angle: 0 },
          ],
        });
      }
    } else if (type.includes('skirt')) {
      const hW = ((hip + 4) / 4) * scale;
      const wW = ((waist + waistEase) / 4) * scale;
      const length = 60 * scale;

      pieces.push({
        id: 'skirt-front',
        name: 'Skirt Front',
        path: `M 0,0 L ${wW},0 L ${hW + 5},${length} L 0,${length} Z`,
        labelPos: { x: wW / 2, y: length / 2 },
        seamAllowance: 1.5,
        grainline: { x1: wW / 2, y1: 20, x2: wW / 2, y2: length - 20 },
        notches: [{ x: wW, y: 0, angle: Math.PI / 2 }],
      });

      pieces.push({
        id: 'skirt-back',
        name: 'Skirt Back',
        path: `M ${wW + 30},0 L ${wW * 2 + 30},0 L ${hW * 2 + 35},${length} L ${wW + 30},${length} Z`,
        labelPos: { x: wW * 1.5 + 30, y: length / 2 },
        seamAllowance: 1.5,
        grainline: { x1: wW * 1.5 + 30, y1: 20, x2: wW * 1.5 + 30, y2: length - 20 },
      });
    } else if (type.includes('pant') || type.includes('trouser') || type.includes('short')) {
      const hW = ((hip + 4) / 4) * scale;
      const wW = ((waist + waistEase) / 4) * scale;
      const inseam = 76 * scale;
      const rise = ((hip / 4) + 2) * scale;

      pieces.push({
        id: 'pants-front',
        name: 'Pants Front',
        path: `M 0,${rise} Q 5,0 ${wW},0 L ${wW},${rise} Q ${hW},${rise + 10} ${hW + 5},${rise + 20} L ${hW - 5},${inseam} L 5,${inseam} Z`,
        labelPos: { x: hW / 2, y: (rise + inseam) / 2 },
        seamAllowance: 1.5,
        grainline: { x1: hW / 2, y1: rise + 30, x2: hW / 2, y2: inseam - 30 },
      });

      pieces.push({
        id: 'pants-back',
        name: 'Pants Back',
        path: `M ${hW + 30},${rise} Q ${hW + 20},0 ${hW * 2 + 30},0 L ${hW * 2 + 30},${rise} Q ${hW * 2 + 40},${rise + 10} ${hW * 2 + 45},${rise + 20} L ${hW * 2 + 25},${inseam} L ${hW + 35},${inseam} Z`,
        labelPos: { x: hW * 1.5 + 30, y: (rise + inseam) / 2 },
        seamAllowance: 1.5,
        grainline: { x1: hW * 1.5 + 30, y1: rise + 30, x2: hW * 1.5 + 30, y2: inseam - 30 },
      });
    } else {
      // Generic fallback: simple rectangle pieces
      pieces.push({
        id: 'piece-front',
        name: 'Front',
        path: `M 0,0 L 240,0 L 240,420 L 0,420 Z`,
        labelPos: { x: 120, y: 210 },
        seamAllowance: 1.5,
        grainline: { x1: 120, y1: 30, x2: 120, y2: 390 },
      });
      pieces.push({
        id: 'piece-back',
        name: 'Back',
        path: `M 270,0 L 510,0 L 510,420 L 270,420 Z`,
        labelPos: { x: 390, y: 210 },
        seamAllowance: 1.5,
        grainline: { x1: 390, y1: 30, x2: 390, y2: 390 },
      });
    }

    return pieces;
  }

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if ((!input.trim() && selectedFiles.length === 0) || isLoading) return;
    
    // Send message with any selected files
    const attachments = selectedFiles.map((file) => ({
      id: crypto.randomUUID(),
      type: file.type.startsWith('image/') ? 'image' : 'document',
      file,
      mediaType: file.type,
    }));
    
    sendMessage(input, attachments);
    setInput('');
    setSelectedFiles([]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setSelectedFiles([...selectedFiles, ...files]);
    }
    // Reset input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  return (
    <div className="flex-1 flex">
      {/* Chat Panel */}
      <div className="flex-1 flex flex-col border-r border-zinc-800">
        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.length === 0 && (
            <div className="h-full flex flex-col items-center justify-center text-center px-8">
              <div className="w-16 h-16 bg-progeny-600/20 rounded-2xl flex items-center justify-center mb-4">
                <FileText className="w-8 h-8 text-progeny-400" />
              </div>
              <h2 className="text-xl font-semibold text-white mb-2">
                Create Your Pattern
              </h2>
              <p className="text-zinc-400 max-w-md mb-6">
                Upload a garment photo, describe your design, or import a tech pack.
                I'll generate production-ready patterns with 3D visualization.
              </p>
              <div className="flex gap-3">
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <Image className="w-4 h-4" /> Upload Photo
                </button>
                <button 
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 rounded-lg text-sm flex items-center gap-2 transition-colors"
                >
                  <FileText className="w-4 h-4" /> Import Tech Pack
                </button>
              </div>
            </div>
          )}
          
          {messages.map((msg) => (
            <ChatMessage key={msg.id} message={msg} />
          ))}
          
          {isLoading && currentJob && (
            <div className="flex items-center gap-3 text-zinc-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>{currentJob.status} {currentJob.progress ? `(${currentJob.progress}%)` : ''}</span>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <div className="p-4 border-t border-zinc-800">
          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-2">
              {selectedFiles.map((file, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-2 bg-zinc-700 px-3 py-2 rounded-lg text-xs"
                >
                  {file.type.startsWith('image/') ? (
                    <Image className="w-3 h-3 text-progeny-400" />
                  ) : (
                    <FileText className="w-3 h-3 text-progeny-400" />
                  )}
                  <span className="text-white">{file.name}</span>
                  <button
                    onClick={() => removeSelectedFile(idx)}
                    className="text-zinc-400 hover:text-white ml-1"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <form onSubmit={handleSubmit} className="relative">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={selectedFiles.length > 0 
                ? "Add a message..." 
                : "Describe your garment or ask a question..."}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-xl px-4 py-3 pr-32 text-white placeholder-zinc-500 focus:outline-none focus:border-progeny-500"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,.pdf"
                onChange={handleFileSelect}
                className="hidden"
                multiple
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                <Paperclip className="w-5 h-5" />
              </button>
              <button
                type="button"
                className="p-2 text-zinc-400 hover:text-white transition-colors"
              >
                <Mic className="w-5 h-5" />
              </button>
              <button
                type="submit"
                disabled={(!input.trim() && selectedFiles.length === 0) || isLoading}
                className={clsx(
                  'p-2 rounded-lg transition-colors',
                  (input.trim() || selectedFiles.length > 0) && !isLoading
                    ? 'bg-progeny-600 text-white hover:bg-progeny-500'
                    : 'bg-zinc-700 text-zinc-500'
                )}
              >
                <Send className="w-5 h-5" />
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Preview Panel */}
      <div className="w-[500px] flex flex-col bg-[#0d0d0f]">
        {/* Tabs */}
        <div className="flex border-b border-zinc-800">
          <button
            onClick={() => setActiveTab('pattern')}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === 'pattern'
                ? 'text-white border-b-2 border-progeny-500'
                : 'text-zinc-400 hover:text-white'
            )}
          >
            Pattern Pieces
          </button>
          <button
            onClick={() => setActiveTab('3d')}
            className={clsx(
              'flex-1 px-4 py-3 text-sm font-medium transition-colors',
              activeTab === '3d'
                ? 'text-white border-b-2 border-progeny-500'
                : 'text-zinc-400 hover:text-white'
            )}
          >
            3D Preview
          </button>
        </div>

        {/* Viewer */}
        <div className="flex-1">
          {activeTab === 'pattern' ? <PatternViewer /> : <Viewer3D />}
        </div>
      </div>
    </div>
  );
}

